import { LitElement, html, css } from 'lit-element';
import dayjs from 'dayjs';
import { HeaderStyles } from '../styles/header-styles';
import { DETAIL_MODES } from '../form/constants';
import { classMap } from 'lit-html/directives/class-map.js';
import { actions, selectors } from './';
import { getRouter } from '../routing/route-base';
import { connect } from '../store/connect';
import '@trumedianetworks/tmn-core-components/src/tmn-grid';
import '@trumedianetworks/tmn-core-components/src/tmn-button';
import '@trumedianetworks/tmn-core-components/src/tmn-radio';
import '@trumedianetworks/tmn-core-components/src/tmn-tabs';
import '@trumedianetworks/tmn-core-components/src/tmn-form-group';
import '../form/form-element-user';
// these could be shared with the package-list formatters, sort of
const dateFormatFn = (value, row) => {
    const jsDate = new Date(parseInt(value, 10));
    return value
        ? html`${dayjs(jsDate).format('YYYY-MM-DD h:MM A')}`
        : html`<div style="text-align:center">-</div>`;
};

const typeFormatter = (packageData = {}) => {
    const { importerType, exporterType } = packageData;

    if (importerType === 'videoConcatenate') {
        return 'MP4';
    } else if (exporterType === 'zip') {
        return 'Offline Playlist';
    } else if (exporterType === 'googleDrive') {
        return 'Google Drive';
    } else {
        return 'other';
    }
};

export class PackageDetail extends LitElement {
    constructor() {
        super();
        this.detailMode = DETAIL_MODES.VIEW;
    }

    static get styles() {
        return [
            HeaderStyles.styles,
            css`
                :host {
                    max-width: 1400px;
                    background: white;
                    display: flex;
                    flex-flow: column;
                    padding: 8px;
                    background-color: var(--app-surface-color);
                    flex: 1 1 100%;
                    min-width: 535px;
                }
                .group {
                    margin: 0 10px 6px 10px;
                }
                .errors {
                    border: 1px solid var(--app-error-color);
                    padding: 8px;
                }
                .errors h2 {
                    color: var(--app-error-color);
                }
                .controls-container {
                    display: flex;
                    flex-wrap: wrap;
                    margin: 6px 6px 12px 6px;
                }

                .controls-container > * {
                    margin-right: 6px;
                    margin-bottom: 6px;
                }
                .fixed-header {
                    background-color: var(--app-surface-color);
                }
                h1 {
                    margin-bottom: 6px;
                }
                @media (max-width: 1024px) {
                    :host {
                        min-width: 0;
                    }
                }
            `,
            // borrowed from form-element
            css`
                tmn-form-group label {
                    font-weight: 600;
                }
                tmn-form-group {
                    user-select: unset;
                }
                tmn-form-group[row] {
                    align-items: center;
                }

                tmn-form-group span {
                    padding: 4px 0px;
                    padding-bottom: 4px;
                    margin-bottom: 4px;
                    font-weight: 400;
                }
                .state-success {
                    font-weight: 600;
                    color: var(--app-success-color);
                }
                .state-failed {
                    color: var(--app-error-color);
                }
            `,
            // borrowed from tmn=package-cell-formatter
            css`
                .error-message {
                    max-width: 400px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                .details-content {
                    display: flex;
                    flex-direction: column;
                    line-height: 1.4;
                }

                .details-row {
                    display: flex;
                    flex-direction: row;
                }

                .description {
                    max-width: 800px;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    vertical-align: bottom;
                }
                .link-error {
                    color: var(--app-error-color);
                    width: max-content;
                }
                ol {
                    list-style-type: decimal;
                    padding-inline-start: 24px;
                }

                li {
                    width: max-content;
                }

                li > div,
                li > a {
                    display: inline-block;
                }
            `,
        ];
    }

    static get properties() {
        return {
            packageData: {
                type: Object,
            },
            isLoading: {
                type: Boolean,
            },
            selectedPackageId: {
                type: Number,
            },
        };
    }

    updated(changedProps) {
        if (changedProps.has('selectedPackageId')) {
            const { selectedPackageId } = this;
            if (selectedPackageId) {
                this.actions.fetchPackageDetails(selectedPackageId);
            }
        }
    }

    render() {
        return html`<div class="fixed-header">${this.renderHeader()}</div>
            ${this.renderMain()}`;
    }

    renderMain() {
        const { isLoading, isSaving } = this;
        if (isLoading || isSaving) {
            return this.renderStatusMessages();
        }

        return html`${this.renderControls()} ${this.renderForm()}`;
    }

    renderForm() {
        const { packageData = {} } = this;
        const { authorId, created, updated, errorMessage, retryCount, retried, sitename, taskId } =
            packageData;

        return html`
            <tmn-grid item container direction="column" spacing="1" class="form-grid">
                <tmn-grid container justify="space-between" alignItems="center" direction="row">
                    <tmn-grid item item sm="3"> ${this.renderState()} </tmn-grid>
                    <tmn-grid item item sm="5">
                        ${this.renderFormElement('email', authorId)}
                    </tmn-grid>
                    <tmn-grid item item sm="4">
                        ${this.renderFormElement('site', sitename)}
                    </tmn-grid>
                </tmn-grid>
                <tmn-grid item container alignItems="center" direction="row">
                    <tmn-grid item sm="3">
                        ${this.renderFormElement('type', typeFormatter(packageData))}
                    </tmn-grid>
                    <tmn-grid item item sm="5">
                        ${this.renderFormElement('created', dateFormatFn(created))}
                    </tmn-grid>
                    <tmn-grid item item sm="4">
                        ${this.renderFormElement('updated', dateFormatFn(updated))}
                    </tmn-grid>
                </tmn-grid>
                <tmn-grid item container alignItems="center" direction="row">
                    <tmn-grid item item sm="3">
                        ${this.renderFormElement('retryCount', retryCount)}
                    </tmn-grid>
                    <tmn-grid item item sm="5">
                        ${this.renderFormElement('taskId', taskId)}
                    </tmn-grid>
                </tmn-grid>

                <tmn-grid item>${this.renderFormElement('Error Message', errorMessage)}</tmn-grid>
                <tmn-grid item> <h3>Details</h3></tmn-grid>
                <tmn-grid item> ${this.renderDetails()}</tmn-grid>
            </tmn-grid>
        `;
    }

    renderDetails() {
        const { packageData = {} } = this;
        const { details } = packageData;
        return html`
            <ol class="details-content">
                ${details?.content.map((c) => {
                    const link = c.data?.[0]?.url;
                    return html`<li>
                        <div class="description" title="${c.description}">${c.description}</div>
                        ${link
                            ? html`<a target="_blank" href="${link}">Link</a>`
                            : html`<div class="link-error">Empty URL</div>`}
                    </li>`;
                })}
            </ol>
        `;
    }

    renderState() {
        const { packageData = {} } = this;
        const { state } = packageData;
        const classes = {
            'state-success': state === 'ready',
            'state-failed': state === 'failed',
        };
        return html`
            <tmn-form-group>
                <label>Status:</label>
                <span class=${classMap(classes)}>${state}</span>
            </tmn-form-group>
        `;
    }

    renderHeader() {
        const { packageData = {} } = this;
        const { name, id } = packageData;
        return html`<h1>${id}: ${name}</h1>`;
    }

    renderStatusMessages() {
        const { isLoading, isSaving } = this;
        if (isLoading) {
            return html`<div>Loading...</div>`;
        }
    }

    renderControls() {
        const { packageData = {}, isLoading } = this;
        const disableForAction = isLoading;
        const { id, name } = packageData;
        const controls = [];
        if (id) {
            // this is a hack to make the "eventData" match the row data from the table
            // for the sake of these buttons
            const eventData = {
                metaData: {
                    id,
                    rawData: {
                        name: name,
                    },
                },
            };
            // should we use the state to decide when/how to show this (i.e. in red if failed or disabled if not failed?)
            controls.push(
                html`<retry-package-btn
                    .controlType=${''}
                    .buttonColor=${''}
                    .name=${name}
                    .eventData=${eventData}
                    @tmn-retry-package-confirmed=${this.triggerRetry}>
                </retry-package-btn>`
            );
        }
        controls.push(
            html`<tmn-button .disabled=${disableForAction} @click=${this.cancel}
                >Close Details</tmn-button
            >`
        );
        return html`<div class="controls-container">${controls}</div> `;
    }

    renderFormElement(displayLabel, value) {
        return html`<tmn-form-group>
            <label>${displayLabel}:</label>
            <span>${value}</span>
        </tmn-form-group>`;
    }

    cancel(e) {
        const router = getRouter();
        router.navigateByName('video-service-list', {});
    }

    triggerRetry(e) {
        //console.log('** triggerRetry', e);
        this.actions.retryPackage(e.detail.metaData.id);
    }
}

const mapStateToProps = (state, ownProps) => {
    return (state) => {
        const packageData = selectors.selectedPackageDataSelector(state);
        return {
            isLoading: selectors.packageListIsLoadingSelector(state),
            packageData,
            selectedPackageId: packageData?.id,
        };
    };
};

customElements.define('package-detail', connect(mapStateToProps, actions)(PackageDetail));
